/**
 * TTL cache pro stats endpointy (`/api/mqtt/today`, `/stats`, `/hourly`). Sbalení/rozbalení
 * HidingWrapperu znovu nestáhne data, dokud TTL neexpiroval.
 *
 * TTL strategie:
 *   - "today" / "hourly" pro aktuální den / hodinu: 60s (data se ještě dotékají)
 *   - "hourly" pro starší den: bez TTL (historický data immutable)
 *   - "history" (range): 5min pro current month, bez TTL pro starší
 *
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 5).
 */

import { create } from "zustand";

/** Klíče v cache.
 *  - `today|<topicId>`
 *  - `history|<topicId>|<start>-<end>|<limit>`  (start/end mohou být undefined)
 *  - `hourly|<topicId>|<dayMs>`
 */
export type StatsKey = string;

export type StatsCacheValue =
  | { kind: "today"; data: dailyStats | null; fetchedAt: number }
  | { kind: "history"; data: dailyStats[]; fetchedAt: number }
  | { kind: "hourly"; data: dailyStats[]; dayMs: number; fetchedAt: number };

interface StatsStore {
  cache: Map<StatsKey, StatsCacheValue>;
  put: (key: StatsKey, value: StatsCacheValue) => void;
  get: (key: StatsKey) => StatsCacheValue | undefined;
  clear: () => void;
}

export const useStatsStore = create<StatsStore>((set, get) => ({
  cache: new Map(),
  put: (key, value) =>
    set((state) => {
      const next = new Map(state.cache);
      next.set(key, value);
      return { cache: next };
    }),
  get: (key) => get().cache.get(key),
  clear: () => set({ cache: new Map() }),
}));

/** Konstanty TTL. */
export const STATS_CURRENT_TTL_MS = 60_000; // 60s pro current day / hour
export const STATS_HISTORY_FRESH_TTL_MS = 5 * 60_000; // 5min pro recent history range

/** Helper — je daný den dnešní? (porovnává podle YYYY-MM-DD timezone-aware). */
export const isToday = (dayMs: number): boolean => {
  const day = new Date(dayMs);
  const now = new Date();
  return (
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate()
  );
};

export const isStatsFresh = (value: StatsCacheValue, now: number): boolean => {
  if (value.kind === "today") return now - value.fetchedAt < STATS_CURRENT_TTL_MS;
  if (value.kind === "hourly") {
    // Pro current day: 60s TTL. Historický den: bez TTL.
    return isToday(value.dayMs) ? now - value.fetchedAt < STATS_CURRENT_TTL_MS : true;
  }
  // history range: 5min "soft" TTL — pokud user nezavře dashboard, refresh občas
  return now - value.fetchedAt < STATS_HISTORY_FRESH_TTL_MS;
};
